import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import IconButton from '@material-ui/core/IconButton';
import ArrowBack from '@material-ui/icons/ArrowBack';
import Alert from '@material-ui/lab/Alert';
import { Paper, makeStyles } from '@material-ui/core';
import Errors from 'components/shared/Errors';
import Success from 'components/shared/Success';
import Main from 'components/templates/Main';
import CircularProgress from 'components/shared/CircularProgress';
import Api from 'api';
import PATHS from 'constants/paths';
import ComplaintForm from '../components/ComplaintForm';
import { updateComplaintRequest, complaintsReset } from '../redux/actionCreators';
import * as selectors from '../redux/selectors';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
}));

const ComplaintEditContainer = () => {
  const classes = useStyles();
  const { id } = useParams();
  const dispatch = useDispatch();
  const intl = useIntl();

  const error = useSelector(selectors.error);
  const isUpdating = useSelector(selectors.isUpdating);
  const conflict = useSelector(selectors.conflict);
  const success = useSelector(selectors.success);

  const [complaint, setComplaint] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError(null);
    setComplaint(null);
    setCategories([]);
    const load = async () => {
      try {
        const [complaintData, categoriesData] = await Promise.all([
          Api.fetchComplaint(id),
          Api.fetchComplaintCategories(),
        ]);
        if (!isMounted) return;
        setComplaint(complaintData);
        setCategories(categoriesData);
        setIsLoading(false);
      } catch (err) {
        if (!isMounted) return;
        setLoadError(
          err.data?.message ||
            intl.formatMessage({
              id: 'complaints.loadError',
              defaultMessage: 'Failed to load data',
            }),
        );
        setIsLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
      dispatch(complaintsReset());
    };
  }, [id]);

  const handleSubmit = (values, files, removedImageIds) => {
    dispatch(
      updateComplaintRequest({
        id,
        data: {
          category_id: Number(values.category_id),
          description: values.description,
          desired_compensation: Number(values.desired_compensation) || 0,
          updated_at: complaint.updated_at,
        },
        files,
        removedImageIds,
      }),
    );
  };

  const backPath = `${PATHS.complaints.root}/${id}`;

  const title = (
    <div>
      <IconButton color="inherit" component={Link} to={backPath}>
        <ArrowBack />
      </IconButton>
      <FormattedMessage id="complaints.editTitle" defaultMessage="Edit complaint" />
    </div>
  );

  if (isLoading) {
    return (
      <Main title={title}>
        <CircularProgress />
      </Main>
    );
  }

  if (loadError) {
    return (
      <Main title={title}>
        <Paper className={classes.paper}>
          <Alert severity="error">{loadError}</Alert>
        </Paper>
      </Main>
    );
  }

  if (complaint && complaint.status !== 'pending_host') {
    return (
      <Main title={title}>
        <Paper className={classes.paper}>
          <Alert severity="warning">
            <FormattedMessage
              id="complaints.cannotEdit"
              defaultMessage="This complaint can no longer be edited"
            />
          </Alert>
        </Paper>
      </Main>
    );
  }

  return (
    <Main title={title}>
      <ComplaintForm
        categories={categories}
        isUpdating={isUpdating}
        conflict={conflict}
        booking={{
          id: complaint.booking_id,
          total_price: complaint.booking?.total_price,
        }}
        onSubmit={handleSubmit}
        editMode
        existingImages={complaint.images}
        initialValues={{
          category_id: String(complaint.category?.id || ''),
          description: complaint.description || '',
          desired_compensation: complaint.desired_compensation || '',
        }}
      />
      <Success success={success} />
      <Errors errors={error} />
    </Main>
  );
};

export default ComplaintEditContainer;
